// File: src/components/Blog/AdminBlogEditor.jsx
// =====================================================
// =============== Section 0: Imports ==================
// =====================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, storage } from '../../modules/auth/firebaseConfig';
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  updateDoc, serverTimestamp, query, orderBy
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, getDownloadURL, uploadBytes, getMetadata
} from 'firebase/storage';
import BlogHeader from './BlogHeader';

import FooterLinks from '../../components/LandingPage/FooterLinks';
import { FaEdit, FaTrash, FaShareAlt } from 'react-icons/fa';
import SocialMediaShare from './SocialMediaShare';

// =====================================================
// ============== Section 1: Small helpers =============
// =====================================================
const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const fileExt = (name = '') => {
  const m = name.split('.');
  return m.length > 1 ? m.pop().toLowerCase() : 'bin';
};

// Escape a string for HTML attribute usage
const esc = (s = '') =>
  String(s).replace(/&/g, '&amp;')
           .replace(/"/g, '&quot;')
           .replace(/</g, '&lt;')
           .replace(/>/g, '&gt;');

// =====================================================
// ===== Section 2: Video → image thumbnail (1s) =======
// =====================================================
const generateVideoThumbnailAndUpload = (videoFile) =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(videoFile);
    const video = document.createElement('video');
    video.src = objectUrl;
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    const cleanup = () => URL.revokeObjectURL(objectUrl);

    const drawAndUpload = async () => {
      try {
        const vw = video.videoWidth || 1280;
        const vh = video.videoHeight || 720;

        const maxW = 1280, maxH = 720;
        const scale = Math.min(maxW / vw, maxH / vh, 1);
        const cw = Math.round(vw * scale);
        const ch = Math.round(vh * scale);

        const canvas = document.createElement('canvas');
        canvas.width = cw; canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, cw, ch);

        canvas.toBlob(async (blob) => {
          if (!blob) { cleanup(); return reject(new Error('No thumbnail blob')); }
          try {
            const path = `blogThumbnails/${Date.now()}.jpg`;
            const storageRef_ = ref(storage, path);
            const task = uploadBytesResumable(storageRef_, blob);
            task.on('state_changed', () => {}, reject, async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              cleanup();
              resolve(url);
            });
          } catch (e) {
            cleanup();
            reject(e);
          }
        }, 'image/jpeg', 0.85);
      } catch (e) {
        cleanup();
        reject(e);
      }
    };

    const trySeek = async () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      const candidates = [1.0, d > 0.1 ? Math.max(0, Math.min(d - 0.05, 1.0)) : null, 0.1, 0.01, 0]
        .filter(t => t !== null && t <= (d || 1e9));

      for (const t of candidates) {
        try {
          await new Promise((res, rej) => {
            const onSeeked = () => { video.removeEventListener('seeked', onSeeked); res(); };
            const onError = () => { video.removeEventListener('seeked', onSeeked); rej(); };
            video.addEventListener('seeked', onSeeked, { once: true });
            video.addEventListener('error', onError, { once: true });
            try { video.currentTime = t; } catch { rej(); }
          });
          return true;
        } catch {}
      }
      return false;
    };

    video.addEventListener('loadedmetadata', async () => {
      try { await video.play(); video.pause(); } catch {}
      const ok = await trySeek();
      if (ok) {
        await drawAndUpload();
      } else {
        setTimeout(() => {
          if (video.readyState >= 2) drawAndUpload();
          else reject(new Error('Could not get a decodable frame'));
        }, 120);
      }
    });

    video.addEventListener('loadeddata', () => {
      setTimeout(() => { if (video.readyState >= 2) drawAndUpload(); }, 200);
    });

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Video load error (codec/permission).'));
    };
  });

// =====================================================
// ========= Section 3: Component & state ==============
// =====================================================
export default function AdminBlogEditor() {
  // Core fields
  const [caption, setCaption] = useState('');
  const [keywords, setKeywords] = useState('');

  const [blocks, setBlocks] = useState([
    { id: newId(), text: '', existingMedia: null, mediaFile: null, mediaObjectUrl: '', currentFileName: '' }
  ]);

  // Thumbnail controls
  const [useAutoThumbnail, setUseAutoThumbnail] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbProgress, setThumbProgress] = useState(0);

  // List & UI
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [visibility, setVisibility] = useState('private');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showSharePanel, setShowSharePanel] = useState(null);

  // File inputs (custom trigger)
  const fileInputRefs = useRef({}); // { [blockId]: HTMLInputElement }

  // ===== Cancel/Reset support =====
  const uploadTasksRef = useRef([]); // active Firebase upload tasks

  const resetForm = () => {
    setCaption('');
    setKeywords('');
    setBlocks([{ id: newId(), text: '', existingMedia: null, mediaFile: null, mediaObjectUrl: '', currentFileName: '' }]);
    setThumbnailFile(null);
    setThumbProgress(0);
    setEditingId(null);
    setVisibility('private');
  };

  const cancelUploads = () => {
    uploadTasksRef.current.forEach(t => { try { t.cancel(); } catch {} });
    uploadTasksRef.current = [];
    setLoading(false);
  };

  // Load posts
  useEffect(() => {
    (async () => {
      const snapshot = await getDocs(query(collection(db, 'post'), orderBy('createdAt', 'desc')));
      setPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    })();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // max Page limit and next page
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(1);

  // Filter once, efficiently
  const filteredPosts = useMemo(
    () =>
      posts.filter((p) =>
        (p.caption || '').toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [posts, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visiblePosts = filteredPosts.slice(start, start + PAGE_SIZE);

  // When search changes, go back to page 1
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Clamp page if posts shrink
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // =====================================================
  // ===== Section 4: Block file preview (client) ========
  // =====================================================
  const handleBlockFile = (idx, file) => {
    setBlocks(prev => {
      const next = [...prev];
      const b = { ...next[idx] };

      b.mediaFile = file || null;
      b.mediaObjectUrl = '';
      if (file) {
        b.mediaObjectUrl = URL.createObjectURL(file);
        b.currentFileName = file.name; // keep selected name visible until changed
      }
      next[idx] = b;
      return next;
    });
  };

  // Try to fill originalName from Storage metadata (for older posts)
  const tryFillOriginalNameFromStorage = async (blockId, url) => {
    try {
      const r = ref(storage, url);
      const meta = await getMetadata(r);
      const orig = meta?.customMetadata?.originalName || '';
      if (orig) {
        setBlocks(prev => prev.map(b =>
          b.id === blockId
            ? { ...b, existingMedia: { ...(b.existingMedia || {}), name: orig }, currentFileName: b.currentFileName || orig }
            : b
        ));
      }
    } catch {
      // ignore if no permission or not set; we'll just show "Existing file"
    }
  };

  // Helper to track cancelable uploads (supports metadata)
  const uploadFileWithProgress = (storageRef_, file, onProgress, metadata) =>
    new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef_, file, metadata);
      uploadTasksRef.current.push(task);
      task.on(
        'state_changed',
        snap => {
          if (onProgress) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            onProgress(pct);
          }
        },
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url });
        }
      );
    });

  // =====================================================
  // ===== Section 5: Upload helpers (Storage) ===========
  // =====================================================
  const handleThumbnailUpload = async () => {
    if (!thumbnailFile) return '';
    const ext = fileExt(thumbnailFile.name);
    const storageRef_ = ref(storage, `blogThumbnails/${Date.now()}.${ext}`);
    const { url } = await uploadFileWithProgress(
      storageRef_,
      thumbnailFile,
      setThumbProgress,
      { contentType: thumbnailFile.type }
    );
    return url;
  };

  const uploadBlockMedia = async (block) => {
    // No new file? keep existing
    if (!block.mediaFile) return block.existingMedia || null;

    const ext = block.mediaFile.name.split('.').pop()?.toLowerCase() || 'bin';
    const storageRef_ = ref(storage, `blogSections/${Date.now()}-${block.id}.${ext}`);

    // Save original filename into Storage metadata; and return it to Firestore
    const metadata = {
      contentType: block.mediaFile.type,
      customMetadata: { originalName: block.mediaFile.name }
    };

    const { url } = await uploadFileWithProgress(storageRef_, block.mediaFile, undefined, metadata);
    return { url, type: block.mediaFile.type, name: block.mediaFile.name };
  };

  // =====================================================
  // === Section 6: HTML Preview generator & upload ======
  // =====================================================
  const uploadHtmlToStorage = async (postId, htmlContent) => {
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const previewRef = ref(storage, `previews/${postId}.html`);
    await uploadBytes(previewRef, htmlBlob);
    return getDownloadURL(previewRef);
  };

  const generatePreviewHtml = async (postId, _caption, _description, _keywords, _thumbnailUrl) => {
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1.0"/>
<title>${esc(_caption)}</title>
<meta name="description" content="${esc(_description)}"/>
<meta name="keywords" content="${esc(_keywords)}"/>
<meta property="og:title" content="${esc(_caption)}"/>
<meta property="og:description" content="${esc(_description)}"/>
<meta property="og:image" content="${esc(_thumbnailUrl)}"/>
<meta property="og:type" content="article"/>
<meta property="og:url" content="https://million2trillion.com/blog/${postId}"/>
<meta name="twitter:card" content="summary_large_image"/>
<meta name="twitter:title" content="${esc(_caption)}"/>
<meta name="twitter:description" content="${esc(_description)}"/>
<meta name="twitter:image" content="${esc(_thumbnailUrl)}"/>
<meta http-equiv="refresh" content="1; url=https://million2trillion.com/blog/${postId}"/></head>
<body><p>Redirecting…</p></body></html>`;
    return uploadHtmlToStorage(postId, html);
  };

  // =====================================================
  // ============ Section 7: Save / Update post ==========
  // =====================================================
  const handlePost = async () => {
    if (!caption.trim() || !keywords.trim()) {
      alert('Enter caption and keywords.');
      return;
    }

    const cleanBlocks = blocks
      .map(b => ({ ...b, text: (b.text || '').trim() }))
      .filter(b => b.text.length > 0 || b.mediaFile || b.existingMedia);

    if (cleanBlocks.length === 0) {
      alert('Add at least one description block (with text and/or media).');
      return;
    }

    setLoading(true);
    uploadTasksRef.current = []; // clear previous

    try {
      // 1) Upload media for each block (keeps order)
      const uploaded = await Promise.all(cleanBlocks.map(uploadBlockMedia));
      const blocksOut = cleanBlocks.map((b, i) => ({
        id: b.id,
        text: b.text,
        media: uploaded[i] || null       // { url, type, name? }
      }));

      // 2) Decide thumbnail
      let thumbnailUrl = '';
      if (!useAutoThumbnail && thumbnailFile) {
        thumbnailUrl = await handleThumbnailUpload();
      } else {
        const firstIdx = blocksOut.findIndex(b => !!b.media);
        if (firstIdx !== -1) {
          const m = blocksOut[firstIdx].media;
          if (m?.type?.startsWith('image/')) {
            thumbnailUrl = m.url;
          } else if (m?.type?.startsWith('video/')) {
            const picked = cleanBlocks[firstIdx].mediaFile;
            if (picked) thumbnailUrl = await generateVideoThumbnailAndUpload(picked);
          }
        }
        if (!thumbnailUrl) {
          const anyImg = uploaded.find(u => u?.type?.startsWith('image/'));
          if (anyImg) thumbnailUrl = anyImg.url;
        }
      }

      // 3) Build payload
      const flatDescription = blocksOut.map(b => b.text).join('\n\n');
      const firstPara = blocksOut[0]?.text?.slice(0, 240) || '';

      const payload = {
        caption,
        keywords,
        description: flatDescription,
        descriptionSections: blocksOut,  // [{id,text,media{url,type,name?}}]
        thumbnailUrl: thumbnailUrl || '',
        visibility,
        createdAt: serverTimestamp()
      };

      // 4) Save
      let postId;
      if (editingId) {
        await updateDoc(doc(db, 'post', editingId), payload);
        postId = editingId;
        setEditingId(null);
      } else {
        const refDoc = await addDoc(collection(db, 'post'), payload);
        postId = refDoc.id;
      }

      // 5) Upload preview HTML for publics
      if (visibility === 'public') {
        await generatePreviewHtml(postId, caption, firstPara, keywords, thumbnailUrl || '');
      }

      // 6) Reset + reload
      resetForm();
      const snapshot = await getDocs(query(collection(db, 'post'), orderBy('createdAt', 'desc')));
      setPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      if (err?.code === 'storage/canceled') {
        alert('Upload canceled.');
      } else {
        alert('Error: ' + (err?.message || err));
      }
    } finally {
      uploadTasksRef.current = [];
      setLoading(false);
    }
  };

  // =====================================================
  // =========== Section 8: Delete / Edit handlers =======
  // =====================================================
  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'post', id));
    const snapshot = await getDocs(query(collection(db, 'post'), orderBy('createdAt', 'desc')));
    setPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleEdit = (post) => {
    setCaption(post.caption || '');
    setKeywords(post.keywords || '');
    setVisibility(post.visibility || 'private');

    if (Array.isArray(post.descriptionSections) && post.descriptionSections.length) {
      const mapped = post.descriptionSections.map(s => ({
        id: s.id || newId(),
        text: s.text || '',
        existingMedia: s.media ? { url: s.media.url, type: s.media.type, name: s.media.name } : null,
        mediaFile: null,
        mediaObjectUrl: '',
        currentFileName: s.media?.name || '' // show saved original name if we have it
      }));
      setBlocks(mapped);

      // For older posts with no saved name, try Storage metadata once
      mapped.forEach(b => {
        if (b.existingMedia && !b.existingMedia.name) {
          tryFillOriginalNameFromStorage(b.id, b.existingMedia.url);
        }
      });
    } else {
      setBlocks([{
        id: newId(),
        text: post.description || '',
        existingMedia: null,
        mediaFile: null,
        mediaObjectUrl: '',
        currentFileName: ''
      }]);
    }

    setEditingId(post.id);
  };

  // =====================================================
  // ================== Section 9: UI ====================
  // =====================================================
  return (
    <div className="bg-black min-h-screen text-white">
      <BlogHeader />

      {/* SEARCH */}
      <section className="text-center py-10 relative">
        <h1 className="text-4xl font-bold">Million2Trillion Blog Editor</h1>
        <div className="relative max-w-md mt-10 mx-auto" ref={inputRef}>
          <div className="flex justify-left bg-gray-900 rounded-md overflow-hidden">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowSuggestions(true); }}
              onClick={() => setShowSuggestions(true)}
              placeholder="Search posts..."
              className="w-full px-4 py-2 bg-gray-900 text-white focus:outline-none"
            />
            <button
              className="px-3 py-2 bg-gray-800 text-white"
              onClick={() => setShowSuggestions(!showSuggestions)}
            >
              ▼
            </button>
          </div>

          {showSuggestions && (
            <ul
              ref={dropdownRef}
              className="absolute left-0 w-full bg-gray-800 border border-t-0 border-gray-600 rounded-b-md shadow-md z-10 max-h-60 overflow-auto"
              style={{ top: '100%', marginTop: 0 }}
            >
              {posts.map((p) => (
                <li
                  key={p.id}
                  onClick={() => { setSearchTerm(p.caption || ''); setShowSuggestions(false); }}
                  className="px-4 py-2 hover:bg-blue-700 cursor-pointer text-left text-white"
                >
                  {p.caption}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* EDITOR */}
      <section className="max-w-3xl mx-auto border rounded-lg p-4">
        {/* Caption */}
        <input
          placeholder="Enter Caption (Title)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          className={`w-full p-3 rounded border mb-3 ${editingId ? 'bg-yellow-800' : 'bg-gray-600'}`}
        />

        {/* Blocks */}
        <div className="space-y-4">
          {blocks.map((b, idx) => (
            <div key={b.id} className="rounded border border-gray-800 p-3 bg-gray-900">
              <label className="block text-sm text-gray-400 mb-1">Paragraph {idx + 1}</label>

              <textarea
                value={b.text}
                onChange={(e) => {
                  const next = blocks.map(x => x.id === b.id ? { ...x, text: e.target.value } : x);
                  setBlocks(next);
                }}
                rows={3}
                className="w-full p-3 rounded border bg-gray-800"
                placeholder="Write paragraph…"
              />

              <div className="mt-3">
                <label className="block text-sm text-gray-400 mb-2">
                  Attach image/video for this paragraph (optional)
                </label>

                {/* Hidden native input + custom trigger */}
                <input
                  ref={(el) => { fileInputRefs.current[b.id] = el; }}
                  type="file"
                  accept="image/*,video/mp4,video/webm"
                  className="hidden"
                  onChange={(e) => handleBlockFile(idx, e.target.files?.[0] || null)}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[b.id]?.click()}
                    className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-600"
                  >
                    {b.mediaFile || b.currentFileName || b.existingMedia ? 'Change file' : 'Choose file'}
                  </button>
                  <span className="text-xs text-gray-300">
                    {b.mediaFile?.name ||
                     b.currentFileName ||
                     (b.existingMedia?.name || (b.existingMedia ? 'Existing file' : 'No file chosen'))}
                  </span>
                </div>

                {(b.mediaObjectUrl || b.existingMedia) && (
                  <div className="mt-3">
                    {(() => {
                      const src = b.mediaObjectUrl || b.existingMedia?.url;
                      const type = b.mediaFile?.type || b.existingMedia?.type || '';

                      if (type.startsWith('image/') || (src && src.match(/\.(png|jpe?g|webp|gif)$/i))) {
                        return (
                          <img
                            src={src}
                            alt=""
                            className="w-full max-h-56 object-contain rounded border border-gray-800"
                          />
                        );
                      }

                      if (type.startsWith('video/')) {
                        return (
                          <video
                            controls
                            playsInline
                            src={src}
                            className="w-full max-h-56 object-contain rounded border border-gray-800"
                          />
                        );
                      }

                      return (
                        <img
                          src={src}
                          alt=""
                          className="w-full max-h-56 object-contain rounded border border-gray-800"
                        />
                      );
                    })()}
                  </div>
                )}
              </div>

              {blocks.length > 1 && (
                <button
                  type="button"
                  onClick={() => setBlocks(blocks.filter(x => x.id !== b.id))}
                  className="text-xs mt-3 px-2 py-1 bg-red-800 hover:bg-red-700 rounded"
                >
                  Remove block
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              setBlocks([
                ...blocks,
                { id: newId(), text: '', existingMedia: null, mediaFile: null, mediaObjectUrl: '', currentFileName: '' }
              ])
            }
            className="text-sm px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 border border-gray-600"
            title="Add new block"
          >
            ＋ Add block
          </button>
        </div>

        {/* Keywords */}
        <textarea
          placeholder="Enter keywords (comma-separated)"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          className="w-full p-2 rounded bg-gray-800 mt-4 mb-3 resize-y min-h-[40px] max-h-[200px]"
        />

        {/* Thumbnail controls */}
        <div className="bg-gray-900 p-3 rounded mb-4">
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={useAutoThumbnail}
              onChange={() => setUseAutoThumbnail(!useAutoThumbnail)}
            />
            Use first block’s media as thumbnail (auto). Uncheck to upload manually.
          </label>

          {!useAutoThumbnail && (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                className="mb-2"
              />
              {thumbnailFile && (
                <div className="mb-2">
                  <p className="text-sm text-gray-400">Selected: {thumbnailFile.name}</p>
                  <img
                    src={URL.createObjectURL(thumbnailFile)}
                    alt="Thumbnail preview"
                    className="w-full max-h-48 object-cover mt-2 rounded border border-gray-800"
                  />
                </div>
              )}
              {thumbProgress > 0 && (
                <div className="w-full bg-gray-600 h-2 rounded mb-2">
                  <div className="h-2 rounded bg-yellow-500" style={{ width: `${thumbProgress}%` }} />
                </div>
              )}
            </>
          )}
        </div>

        {/* Visibility & submit */}
        <div className="flex items-center gap-4 my-2">
          <label className="flex gap-2 items-center">
            <input
              type="radio"
              name="visibility"
              value="public"
              checked={visibility === 'public'}
              onChange={() => setVisibility('public')}
            />
            Public
          </label>
          <label className="flex gap-2 items-center">
            <input
              type="radio"
              name="visibility"
              value="private"
              checked={visibility === 'private'}
              onChange={() => setVisibility('private')}
            />
            Only Me
          </label>

          <button
            onClick={handlePost}
            disabled={loading}
            className={`border px-4 py-2 rounded ${editingId ? 'bg-yellow-700 hover:bg-yellow-600' : 'bg-black hover:bg-blue-700'} border-gray-900`}
          >
            {editingId ? 'Update Post' : loading ? 'Posting…' : 'Upload'}
          </button>

          {/* Cancel / Reset */}
          <button
            onClick={() => (loading ? cancelUploads() : resetForm())}
            className="border px-4 py-2 rounded border-gray-900 hover:bg-gray-800"
          >
            {loading ? 'Cancel Upload' : 'Reset'}
          </button>
        </div>
      </section>

      {/* POSTS LIST */}
      <section className="max-w-full mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 border p-8 justify-left rounded-2xl gap-8">
          {visiblePosts.map((post) => (
            <div key={post.id} className="bg-black border border-gray-900 p-4  rounded-lg shadow-md text-left">
              {post.thumbnailUrl && (
                <img
                  src={post.thumbnailUrl}
                  alt=""
                  className="w-full max-h-max object-contain rounded border border-gray-900"
                />
              )}

              <div className="mt-2">
                <p className="mb-2 font-semibold">{post.caption}</p>
                {Array.isArray(post.descriptionSections) && post.descriptionSections.length > 0 ? (
                  <p className="text-sm text-gray-300 mb-2">
                    {post.descriptionSections[0].text}
                    {post.descriptionSections.length > 1 ? ' …' : ''}
                  </p>
                ) : (
                  post.description && <p className="text-sm text-gray-300 mb-2">{post.description}</p>
                )}
                {post.keywords && (
                  <p className="text-sm text-gray-400 mb-2">#{post.keywords.split(',').join(' #')}</p>
                )}
              </div>

              <div className="mt-4 flex gap-4 text-xl mb-2">
                <FaEdit className="cursor-pointer hover:bg-blue-600 hover:border hover:rounded-sm hover:p-1" onClick={() => handleEdit(post)} />
                <FaTrash className="cursor-pointer hover:bg-blue-600 hover:border hover:rounded-sm hover:p-1" onClick={() => handleDelete(post.id)} />
                {post.visibility === 'public' && (
                  <FaShareAlt
                    className="cursor-pointer hover:bg-blue-600 hover:border hover:rounded-sm hover:p-1"
                    onClick={() => setShowSharePanel(showSharePanel === post.id ? null : post.id)}
                  />
                )}
                  {/* NEW: Preview button (links to BlogDetail.jsx route) */}
                   <a
                    href={`/blog/${post.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-sm px-3 py-0 rounded bg-gray-900 hover:bg-blue-600 text-white"
                    title="Open blog post preview"
                   >
                  Preview
                 </a>
              </div>

              {showSharePanel === post.id && <SocialMediaShare postId={post.id} />}

              {post.createdAt?.seconds && (
                <p className="text-xs text-gray-500 mt-2">
                  Posted on {new Date(post.createdAt.seconds * 1000).toLocaleString()}
                </p>
              )}

              {post.visibility === 'public' && (
                <a
                  href={`https://million2trillion-blog.web.app/previews/${post.id}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-400 text-sm underline hover:text-green-300"
                >
                  🔗 View Meta Preview
                </a>
              )}
                       
          </div>
          ))}
        </div>

        {/* Pagination controls */}
        <div className="flex items-center justify-between mt-8 mb-24">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`px-4 py-2 rounded border border-gray-700 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
          >
            ← Prev
          </button>

          <span className="text-sm text-gray-300">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded border border-gray-700 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'}`}
          >
            Next →
          </button>
        </div>
      </section>
      

      <FooterLinks />
    </div>
  );
}
